import { Observable, Subject } from "rxjs";
import { share, takeUntil } from "rxjs/operators";
import ResizeObserver from "resize-observer-polyfill";

export class ResizeObserverHelper {
	public static get ResizeObserver() {
		return window.ResizeObserver ?? ResizeObserver;
	}
	
	private static readonly observer = new this.ResizeObserver(ResizeObserverHelper.handleResize);
	private static readonly registrations = new Map<Element, ResizeObserverRegistration>();
	
	static observe(element: Element) {
		let registration = this.registrations.get(element) ?? ResizeObserverHelper.createRegistration(element);
		return registration.observable$!;
	}
	
	static unobserveAll() {
		this.observer.disconnect();
		this.registrations.forEach(reg => reg.complete());
		this.registrations.clear();
	}
	
	static unobserveElement(element: Element) {
		this.registrations.get(element)?.complete();
		this.observer.unobserve(element);
	}
	
	private static unobserve(registration: ResizeObserverRegistration) {
		this.observer.unobserve(registration.element);
		this.registrations.delete(registration.element);
	}
	
	private static addRegistration(registration: ResizeObserverRegistration) {
		this.observer.observe(registration.element);
	}
	
	private static createRegistration(element: Element) {
		const registration = new ResizeObserverRegistration(element);
		ResizeObserverHelper.registrations.set(element, registration);
		
		registration.observable$ = new Observable<ResizeObserverEntry>(subscriber => {
			registration.callback = entry => subscriber.next(entry);
			ResizeObserverHelper.addRegistration(registration);
			
			subscriber.add(() => ResizeObserverHelper.unobserve(registration));
		})
			.pipe(
				takeUntil(registration.complete$),
				share()
			);
		
		return registration;
	}
	
	private static handleResize(this: undefined, entries: ResizeObserverEntry[], observer: ResizeObserver) {
		entries.forEach(entry => {
			const registration = ResizeObserverHelper.registrations.get(entry.target);
			if (!registration) {
				// Unobserve any observed element that has no registration
				observer.unobserve(entry.target);
				return;
			}
			
			try {
				registration.callback?.(entry);
			}
			catch (e) {
				console.error('Caught error in ResizeObserver callback', e);
				ResizeObserverHelper.unobserve(registration);
			}
		})
	}
}

class ResizeObserverRegistration {
	callback?: (entry: ResizeObserverEntry) => void;
	observable$?: Observable<ResizeObserverEntry>;
	complete$ = new Subject();
	
	constructor(
		public readonly element: Element,
	) {}
	
	complete() {
		this.complete$.next();
	}
}